import { useCallback } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	ExtensionConfiguration,
	SelectionExtensionPlugin,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { expVal } from '@confluence/feature-experiments';

import { messages } from '../messages';

export type UseCreateSnippetExtensionProps = {
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

export const useCreateSnippetExtension = ({
	editorAPIRef,
}: UseCreateSnippetExtensionProps): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireCreateSnippetButtonClickedAnalytics = useCallback(
		(selectionLength: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						selectionLength,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleCreateSnippet = useCallback(() => {
		if (!editorAPIRef?.current) {
			return;
		}

		const selectionDocument =
			editorAPIRef?.current?.selectionExtension?.actions.getDocumentFromSelection();
		const selectionJson = JSON.stringify(selectionDocument?.selectedNodeAdf);

		if (!selectionJson) {
			return;
		}

		fireCreateSnippetButtonClickedAnalytics(selectionJson.length);
	}, [editorAPIRef, fireCreateSnippetButtonClickedAnalytics]);

	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createTemplate),
					icon: TemplateIcon,
					onClick: () => {
						void handleCreateSnippet();
					},
				},
			];
		},
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
		},
	];
};
